export const THEME_STORAGE_KEY = "election-atlas-theme";
export const DEFAULT_THEME_MODE = "dark";
export const THEME_MODES = {
  dark: "dark",
  light: "light",
};

export const darkPalette = {
  brand: "#ffbf00",
  brandForeground: "#000000",
  background: "#0a0a0a",
  backgroundAccent: "#141414",
  foreground: "#fafafa",
  card: "#0a0a0a",
  popover: "#0a0a0a",
  primary: "#fafafa",
  primaryForeground: "#171717",
  secondary: "#262626",
  secondaryForeground: "#fafafa",
  muted: "#262626",
  mutedForeground: "#a1a1a1",
  border: "#262626",
  input: "#262626",
  ring: "#525252",
  destructive: "#82181a",
  destructiveForeground: "#fb2c36",
  textMuted: "#969696",
  textSubtle: "#767676",
  chartOrange: "#f05100",
  chartTeal: "#009588",
  chartBlue: "#104e64",
  chartYellow: "#fcbb00",
  chartAmber: "#f99c00",
  logoBlue: "#6F94B6",
  logoCoral: "#E06F69",
  black: "#000000",
  white: "#ffffff",
};

export const lightPalette = {
  brand: "#e0a800",
  brandForeground: "#000000",
  background: "#ffffff",
  backgroundAccent: "#f7f6f3",
  foreground: "#0a0a0a",
  card: "#ffffff",
  popover: "#ffffff",
  primary: "#0a0a0a",
  primaryForeground: "#f7f6f3",
  secondary: "#ececea",
  secondaryForeground: "#0a0a0a",
  muted: "#ececea",
  mutedForeground: "#525252",
  border: "#d9d9d6",
  input: "#ececea",
  ring: "#a1a1a1",
  destructive: "#fde7e7",
  destructiveForeground: "#b1181b",
  textMuted: "#525252",
  textSubtle: "#767676",
  chartOrange: "#d94800",
  chartTeal: "#007a70",
  chartBlue: "#0c3f52",
  chartYellow: "#e0a800",
  chartAmber: "#d98b00",
  logoBlue: "#5A7FA3",
  logoCoral: "#C2554F",
  black: "#000000",
  white: "#ffffff",
};

export const themeFonts = {
  sans: "'Inter', 'Inter Fallback', sans-serif",
  mono:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
};

export const atlasPalettes = {
  [THEME_MODES.dark]: darkPalette,
  [THEME_MODES.light]: lightPalette,
};

const isThemeMode = (themeMode) =>
  themeMode === THEME_MODES.dark || themeMode === THEME_MODES.light;

export const normalizeThemeMode = (themeMode) =>
  isThemeMode(themeMode) ? themeMode : DEFAULT_THEME_MODE;

export const getStoredThemeMode = () => {
  if (typeof window === "undefined") {
    return DEFAULT_THEME_MODE;
  }

  return normalizeThemeMode(window.localStorage.getItem(THEME_STORAGE_KEY));
};

export const storeThemeMode = (themeMode) => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(THEME_STORAGE_KEY, normalizeThemeMode(themeMode));
};

export const applyAtlasTheme = (themeMode) => {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.dataset.atlasTheme =
    normalizeThemeMode(themeMode);
};

const hexToRgb = (hex) => {
  const normalized = hex.replace("#", "");
  const numeric = parseInt(normalized, 16);

  return {
    r: (numeric >> 16) & 255,
    g: (numeric >> 8) & 255,
    b: numeric & 255,
  };
};

const alpha = (hex, opacity) => {
  const { r, g, b } = hexToRgb(hex);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const getPaletteForTheme = (themeMode) =>
  atlasPalettes[normalizeThemeMode(themeMode)];

export const getAtlasTheme = (themeMode = DEFAULT_THEME_MODE) => {
  const palette = getPaletteForTheme(themeMode);

  return {
    cssVar: {
      key: `election-atlas-${normalizeThemeMode(themeMode)}`,
    },
    token: {
      colorPrimary: palette.brand,
      colorPrimaryHover: alpha(palette.brand, 0.9),
      colorPrimaryActive: alpha(palette.brand, 0.8),
      colorLink: palette.brand,
      colorLinkHover: alpha(palette.brand, 0.9),
      colorLinkActive: alpha(palette.brand, 0.8),
      colorInfo: palette.chartBlue,
      colorSuccess: palette.chartTeal,
      colorWarning: palette.chartAmber,
      colorError: palette.destructiveForeground,

      fontFamily: themeFonts.sans,
      fontFamilyCode: themeFonts.mono,
      fontSize: 14,
      fontWeightStrong: 700,
      borderRadius: 2,
      borderRadiusXS: 2,
      borderRadiusSM: 2,
      borderRadiusLG: 8,

      colorBgBase: palette.background,
      colorBgContainer: palette.backgroundAccent,
      colorBgElevated: palette.popover,
      colorBgLayout: palette.background,

      colorText: palette.foreground,
      colorTextBase: palette.foreground,
      colorTextLightSolid: palette.white,
      colorTextSecondary: palette.textMuted,
      colorTextTertiary: palette.textSubtle,
      colorTextQuaternary: alpha(palette.foreground, 0.38),

      colorBorder: palette.border,
      colorBorderSecondary: alpha(palette.foreground, 0.1),
      colorSplit: alpha(palette.foreground, 0.1),
      colorFill: palette.secondary,
      colorFillSecondary: alpha(palette.foreground, 0.08),
      colorFillTertiary: alpha(palette.foreground, 0.06),
      controlItemBgHover: palette.secondary,
      controlOutline: alpha(palette.brand, 0.35),
      controlTmpOutline: alpha(palette.brand, 0.18),
    },
    components: {
      Button: {
        borderRadius: 2,
        defaultBg: "transparent",
        defaultBorderColor: palette.brand,
        defaultColor: palette.brand,
        defaultHoverBg: alpha(palette.brand, 0.1),
        defaultHoverBorderColor: palette.brand,
        defaultHoverColor: palette.brand,
        fontWeight: 700,
        primaryColor: palette.brandForeground,
      },
      Card: {
        colorBgContainer: palette.card,
        colorBorderSecondary: palette.border,
        colorTextDescription: palette.textMuted,
        borderRadiusLG: 8,
      },
      Form: {
        labelColor: palette.textMuted,
      },
      Input: {
        activeBorderColor: palette.brand,
        activeShadow: `0 0 0 3px ${alpha(palette.brand, 0.18)}`,
        colorBgContainer: palette.input,
        colorTextPlaceholder: alpha(palette.foreground, 0.4),
        hoverBorderColor: palette.brand,
      },
      Layout: {
        bodyBg: palette.background,
        footerBg: palette.background,
        headerBg: palette.background,
        headerColor: palette.foreground,
        siderBg: palette.background,
      },
      Menu: {
        itemBg: "transparent",
        itemColor: palette.foreground,
        itemHoverBg: palette.secondary,
        itemHoverColor: palette.brand,
        itemSelectedBg: alpha(palette.brand, 0.12),
        itemSelectedColor: palette.brand,
      },
      Modal: {
        contentBg: palette.backgroundAccent,
        headerBg: palette.backgroundAccent,
        titleColor: palette.foreground,
      },
      Cascader: {
        optionSelectedBg: alpha(palette.brand, 0.12),
      },
      Select: {
        activeBorderColor: palette.brand,
        optionActiveBg: palette.secondary,
        optionSelectedBg: alpha(palette.brand, 0.12),
        optionSelectedColor: palette.brand,
      },
      Table: {
        borderColor: palette.border,
        colorBgContainer: palette.backgroundAccent,
        headerBg: palette.secondary,
        headerColor: palette.foreground,
        rowHoverBg: alpha(palette.foreground, 0.06),
      },
    },
  };
};

const theme = getAtlasTheme(DEFAULT_THEME_MODE);

export default theme;
